import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GraduationCap, Loader2, Check, Calendar, X as XIcon, ShieldCheck } from "lucide-react";
import { getPublicDrive, publicDriveDecision } from "@/lib/campusApi";
import WishireLogo from "@/components/wishire/WishireLogo";

const STATUS_STYLE = {
  applied: "bg-stone-100 text-stone-600",
  shortlisted: "bg-blue-100 text-blue-700",
  interview: "bg-amber-100 text-amber-700",
  offer: "bg-purple-100 text-purple-700",
  placed: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-600",
};

function ScorePill({ score }) {
  const c = score >= 70 ? "#15803D" : score >= 40 ? "#D97706" : "#C2410C";
  return <span className="font-mono text-lg font-bold" style={{ color: c }}>{score}</span>;
}

export default function RecruiterShortlist() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const { data } = await getPublicDrive(token); setData(data); }
    catch (e) { setErr(e?.response?.data?.detail || "This shortlist link is invalid or expired."); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const decide = async (app_id, action) => {
    setBusy(app_id + action);
    setData((d) => ({ ...d, applicants: d.applicants.map((a) => a.app_id === app_id ? { ...a, status: action === "reject" ? "rejected" : action } : a) }));
    try { await publicDriveDecision(token, app_id, action); } catch (_) { load(); }
    finally { setBusy(null); }
  };

  if (err) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6"><div className="rounded-3xl border border-stone-200 bg-white p-10 text-center text-stone-600 max-w-md"><p className="font-display text-2xl mb-2">Link unavailable</p><p className="text-sm">{err}</p></div></div>;
  if (!data) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center text-stone-500"><Loader2 className="animate-spin mr-2" size={18} /> Loading shortlist…</div>;

  const d = data.drive;
  return (
    <div className="min-h-screen bg-[#FDFBF7] wsh-grain">
      <div className="container-x py-10 md:py-14 max-w-5xl">
        <div className="flex items-center justify-between mb-8">
          <WishireLogo size="md" />
          <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-stone-500"><GraduationCap size={13} /> {d.college_name}</span>
        </div>

        <div className="rounded-3xl bg-stone-900 text-stone-50 p-8 md:p-10 relative overflow-hidden wsh-grain mb-8">
          <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(500px 320px at 88% 12%, rgba(217,119,6,0.45), transparent 60%)" }} />
          <div className="relative">
            <p className="text-xs font-mono uppercase tracking-widest text-amber-300">{d.company_name}</p>
            <h1 className="font-display text-3xl md:text-4xl mt-2">{d.title}</h1>
            <p className="mt-2 text-stone-300">{d.role}{d.ctc ? ` · ${d.ctc}` : ""} · {data.applicants.length} candidate{data.applicants.length === 1 ? "" : "s"}, ranked by Wishire Score</p>
          </div>
        </div>

        {data.applicants.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-stone-300 bg-white p-12 text-center text-stone-500">No applicants yet.</div>
        ) : (
          <div className="space-y-3">
            {data.applicants.map((a) => (
              <div key={a.app_id} className="rounded-2xl border border-stone-200 bg-white p-5 flex flex-wrap items-center gap-4">
                <div className="w-12 text-center"><ScorePill score={a.wishire_score} /><p className="text-[9px] font-mono uppercase text-stone-400">score</p></div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-stone-900 flex items-center gap-1.5">{a.student_name}{a.cgpa_verified && <span className="inline-flex items-center gap-0.5 text-[10px] font-mono uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full"><ShieldCheck size={10} /> Verified</span>}</p>
                  <p className="text-xs text-stone-500">{a.branch || "—"}{a.cgpa != null ? ` · CGPA ${a.cgpa}` : ""} · résumé {a.resume_score ?? "—"} · interview {a.interview_score ?? "—"}</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[a.status] || STATUS_STYLE.applied}`}>{a.status}</span>
                <div className="flex gap-2">
                  <button onClick={() => decide(a.app_id, "shortlist")} disabled={busy} title="Shortlist" className="p-2 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50"><Check size={15} /></button>
                  <button onClick={() => decide(a.app_id, "interview")} disabled={busy} title="Interview" className="p-2 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-50"><Calendar size={15} /></button>
                  <button onClick={() => decide(a.app_id, "reject")} disabled={busy} title="Reject" className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"><XIcon size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-8 text-center text-xs text-stone-400">Powered by Wishire · scores combine résumé, AI interview, verified skills &amp; more.</p>
      </div>
    </div>
  );
}
